import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  CircularProgress,
  Typography,
  Checkbox,
  Paper,
  Box,
  Chip,
  Divider,
  Avatar,
  useTheme,
} from '@mui/material';
import { Search, CheckCircle, Truck } from 'lucide-react';
import { getEquipments } from 'src/queries/query';

const getEquipmentUuid = (equipment) =>
  equipment?.uuid || equipment?.equipmentUuid || equipment?.id || null;

export default function AddEquipmentModal({
  open,
  handleClose,
  leg,
  onAssign,
  isSaving = false,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState({}); // { uuid: true/false }

  const originallyAssignedUuids = useMemo(
    () =>
      (Array.isArray(leg?.equipments) ? leg.equipments : [])
        .map(getEquipmentUuid)
        .filter(Boolean),
    [leg?.equipments],
  );

  useEffect(() => {
    if (open) {
      setSearchTerm('');

      if (leg?.equipments && Array.isArray(leg.equipments)) {
        const preselected = {};
        leg.equipments.forEach((equipment) => {
          const uuid = getEquipmentUuid(equipment);
          if (uuid) {
            preselected[uuid] = true;
          }
        });
        setSelectedEquipment(preselected);
      } else {
        setSelectedEquipment({});
      }

      fetchEquipment();
    }
  }, [open, leg?.equipments]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const result = await getEquipments();
      const fetchedEquipment = Array.isArray(result?.data?.data?.data)
        ? result?.data?.data?.data
        : [];
      setEquipmentList(fetchedEquipment);
    } catch (error) {
      console.error('Error fetching equipment:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredEquipment = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return equipmentList.filter((equipment) => {
      const name =
        typeof equipment?.name === 'string' ? equipment.name.toLowerCase() : '';
      const type =
        typeof equipment?.equipmentType?.name === 'string'
          ? equipment.equipmentType.name.toLowerCase()
          : typeof equipment?.type?.name === 'string'
            ? equipment.type.name.toLowerCase()
            : '';
      return name.includes(lowerSearch) || type.includes(lowerSearch);
    });
  }, [equipmentList, searchTerm]);

  const selectedUuids = useMemo(
    () =>
      Object.keys(selectedEquipment).filter((uuid) => selectedEquipment[uuid]),
    [selectedEquipment],
  );

  const toAdd = useMemo(
    () =>
      selectedUuids.filter((uuid) => !originallyAssignedUuids.includes(uuid)),
    [selectedUuids, originallyAssignedUuids],
  );

  const toRemove = useMemo(
    () =>
      originallyAssignedUuids.filter((uuid) => !selectedUuids.includes(uuid)),
    [originallyAssignedUuids, selectedUuids],
  );

  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const isEquipmentAssigned = (equipmentUuid) => {
    return originallyAssignedUuids.includes(equipmentUuid);
  };

  const handleCheckboxChange = (uuid) => (event) => {
    setSelectedEquipment((prev) => ({
      ...prev,
      [uuid]: event.target.checked,
    }));
  };

  const handleAssignEquipment = () => {
    if (!leg?.uuid || !hasChanges) {
      return;
    }
    onAssign(leg.uuid, { toAdd, toRemove, selectedUuids });
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'primary.contrastText',
            color: 'primary.main',
            width: 32,
            height: 32,
          }}
        >
          <Truck size={18} />
        </Avatar>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem' }}>
            Assign Equipment to Leg: {leg?.legNumber}
          </Typography>
          {originallyAssignedUuids.length > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {originallyAssignedUuids.length} already assigned
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.5, bgcolor: 'grey.50' }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
            }}
          >
            <Search size={18} color={theme.palette.text.secondary} />
            <Input
              type="text"
              placeholder="Search equipment..."
              fullWidth
              disableUnderline
              sx={{ ml: 1.5, fontSize: '0.9rem' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Paper>
        </Box>

        <Divider />

        <Box sx={{ p: 2, minHeight: 350, maxHeight: 500, overflowY: 'auto' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 250,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}
              >
                {searchTerm ? 'Search Results' : 'Available Equipment'}
                {!searchTerm && (
                  <Chip
                    label={filteredEquipment.length}
                    size="small"
                    sx={{ ml: 1.5, height: 20 }}
                  />
                )}
              </Typography>

              {filteredEquipment.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {filteredEquipment.map((equipment) => {
                    const equipmentUuid = getEquipmentUuid(equipment);
                    const isAssigned = isEquipmentAssigned(equipmentUuid);
                    const isSelected = !!selectedEquipment[equipmentUuid];

                    return (
                      <Paper
                        key={equipmentUuid}
                        elevation={isSelected ? 2 : 1}
                        sx={{
                          p: 1.5,
                          border: isSelected ? 2 : 1,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderRadius: 1.5,
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            elevation: 2,
                            borderColor: 'primary.light',
                          },
                          bgcolor: isSelected
                            ? 'primary.50'
                            : 'background.paper',
                        }}
                        onClick={() => {
                          if (!equipmentUuid || isSaving) return;
                          setSelectedEquipment((prev) => ({
                            ...prev,
                            [equipmentUuid]: !prev[equipmentUuid],
                          }));
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isSaving}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCheckboxChange(equipmentUuid)(e);
                            }}
                            sx={{
                              color: 'primary.main',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                              p: 0.5,
                            }}
                            size="small"
                          />

                          <Avatar
                            sx={{
                              bgcolor: isAssigned ? 'success.main' : 'grey.200',
                              color: isAssigned
                                ? 'success.contrastText'
                                : 'text.secondary',
                              width: 36,
                              height: 36,
                            }}
                          >
                            <Truck size={18} />
                          </Avatar>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.25,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                              >
                                {equipment?.name}
                              </Typography>
                              {isAssigned && (
                                <Chip
                                  icon={<CheckCircle size={14} />}
                                  label="Assigned"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}
                            >
                              {equipment?.equipmentType?.name ||
                                equipment?.type?.name ||
                                'N/A'}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{
                                fontSize: '0.75rem',
                                display: 'block',
                                mt: 0.25,
                              }}
                            >
                              VIN: {equipment?.vin || 'N/A'} | License:{' '}
                              {equipment?.licensePlate || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'text.secondary',
                  }}
                >
                  <Truck size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontSize: '1rem' }}
                  >
                    {searchTerm
                      ? 'No equipment matches your search criteria'
                      : 'No equipment available'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, fontSize: '0.85rem' }}
                  >
                    {searchTerm && 'Try adjusting your search terms'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
        <Button
          onClick={handleClose}
          size="medium"
          color="error"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssignEquipment}
          variant="contained"
          size="medium"
          disabled={!hasChanges || isSaving || !leg?.uuid}
          sx={{
            px: 3,
            py: 0.75,
            minWidth: 120,
          }}
        >
          {isSaving ? (
            <CircularProgress size={22} color="inherit" />
          ) : toRemove.length > 0 && toAdd.length === 0 ? (
            `Unassign ${toRemove.length}`
          ) : toAdd.length > 0 && toRemove.length === 0 ? (
            `Assign ${toAdd.length}`
          ) : hasChanges ? (
            'Save Changes'
          ) : (
            'Select'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
